import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { PanHorizontalIcon } from '@dynatrace/strato-icons';

import { allowWhenKeyIsEnter } from './utils.js';
import { KeyboardShortcut } from '../../../../content/keyboard-shortcut/KeyboardShortcut.js';
import { KeyboardControls } from '../../../core/components/interactions-overlay/constants.js';
import { INTERACTION_MODE } from '../../../core/components/interactions-overlay/types.js';
import { useSynchronizedSelection } from '../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { TOOLBAR_MESSAGES } from '../../../core/components/toolbar/messages.js';
import { styledOrText } from '../../../core/components/toolbar/styles.sty.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import { XYCHART_MESSAGES } from '../../messages/messages.js';
import { reduceInteractionMode } from '../../store/reduce-interaction-mode.js';
import { reduceExploreCancelAll } from '../../store/reducer-explorer.js';
import {
  useInteractionModeState,
  useSetState,
  useZoomState,
} from '../../store/selectors.js';

export const PanButton = () => {
  const setState = useSetState();
  const panButtonRef = useRef<HTMLButtonElement>(null);
  const intl = useIntl();
  const tabIndex = useA11yToolbarTabIndex(INTERACTION_MODE.Pan);
  const { zoomOutEnabled } = useZoomState();
  const interactionMode = useInteractionModeState();
  const { resetSelectionState } = useSynchronizedSelection();

  const isSelected = interactionMode === INTERACTION_MODE.Pan;

  const action = () => {
    resetSelectionState?.();
    setState(reduceExploreCancelAll);
    setState(reduceInteractionMode(INTERACTION_MODE.Pan));
  };

  return (
    <ChartToolbarRenderer.Control
      ref={panButtonRef}
      prefixIcon={<PanHorizontalIcon />}
      tooltipText={intl.formatMessage(XYCHART_MESSAGES.zoomModePan)}
      menuShortcut={
        <>
          <KeyboardShortcut
            keys="alt"
            variant="minimal"
            disabled={!zoomOutEnabled}
          />
          <span className={styledOrText}>
            {` ${intl.formatMessage(TOOLBAR_MESSAGES.or)} `}
          </span>
          <KeyboardShortcut
            keys={KeyboardControls.KEYBOARD_PAN_MODE_SHORTCUT_ALTERNATIVE}
            variant="minimal"
            disabled={!zoomOutEnabled}
          />
        </>
      }
      selected={isSelected}
      onKeyDown={allowWhenKeyIsEnter(action)}
      onClick={action}
      tabIndex={tabIndex}
      disabled={!zoomOutEnabled}
      placement="menu"
      data-dt-name="ToolbarPanMode"
    />
  );
};

PanButton['displayName'] = 'PanButton';
